import { DependencyList, useCallback, useEffect, useRef, useState } from 'react'

const DEFAULT_ROOT_MARGIN = '120px'
const FIRST_PAGE = 1
const NO_PAGE_LOADED = FIRST_PAGE - 1

export interface InfiniteScrollPage<T> {
  items: T[]
  hasMoreRecordsInScope: boolean
}

type FetchPage<T> = (page: number, signal: AbortSignal) => Promise<InfiniteScrollPage<T>>

interface Options {
  rootMargin?: string
}

export interface UseInfiniteScroll<T> {
  items: T[]
  loading: boolean
  hasMoreRecordsInScope: boolean
  error: boolean
  rootRef: (node: HTMLElement | null) => void
  sentinelRef: (node: HTMLElement | null) => void
  loadMore: () => void
}

export function useInfiniteScroll<T>(
  fetchPage: FetchPage<T>,
  deps: DependencyList,
  options: Options = {}
): UseInfiniteScroll<T> {
  const { rootMargin = DEFAULT_ROOT_MARGIN } = options

  const [items, setItems] = useState<T[]>([])
  const [lastLoadedPage, setLastLoadedPage] = useState(NO_PAGE_LOADED)
  const [hasMoreRecordsInScope, setHasMoreRecordsInScope] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [sentinelNode, setSentinelNode] = useState<HTMLElement | null>(null)

  const rootNodeRef = useRef<HTMLElement | null>(null)
  const inFlightRequestRef = useRef<AbortController | null>(null)

  const fetchPageRef = useRef(fetchPage)
  useEffect(() => {
    fetchPageRef.current = fetchPage
  })

  const loadPage = useCallback(async (nextPage: number) => {
    inFlightRequestRef.current?.abort()
    const request = new AbortController()
    inFlightRequestRef.current = request

    setLoading(true)
    setError(false)
    try {
      const result = await fetchPageRef.current(nextPage, request.signal)
      if (request.signal.aborted) return
      setItems(prev => (nextPage === FIRST_PAGE ? result.items : [...prev, ...result.items]))
      setLastLoadedPage(nextPage)
      setHasMoreRecordsInScope(result.hasMoreRecordsInScope)
      setLoading(false)
    } catch {
      if (request.signal.aborted) return
      setError(true)
      setLoading(false)
    }
  }, [])

  const resetAndLoadFirstPage = useCallback(() => {
    setItems([])
    setLastLoadedPage(NO_PAGE_LOADED)
    setHasMoreRecordsInScope(true)
    loadPage(FIRST_PAGE)
  }, [loadPage])

  useEffect(() => {
    resetAndLoadFirstPage()
    return () => inFlightRequestRef.current?.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  const loadMore = useCallback(() => {
    if (loading || error || !hasMoreRecordsInScope) return
    loadPage(lastLoadedPage + 1)
  }, [loading, error, hasMoreRecordsInScope, lastLoadedPage, loadPage])

  const loadMoreRef = useRef(loadMore)
  useEffect(() => {
    loadMoreRef.current = loadMore
  })

  useEffect(() => {
    if (!sentinelNode) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) loadMoreRef.current()
      },
      { root: rootNodeRef.current, rootMargin }
    )

    observer.observe(sentinelNode)

    return () => observer.disconnect()
  }, [sentinelNode, rootMargin])

  const rootRef = useCallback((node: HTMLElement | null) => {
    rootNodeRef.current = node
  }, [])

  const sentinelRef = useCallback((node: HTMLElement | null) => {
    setSentinelNode(node)
  }, [])

  return { items, loading, hasMoreRecordsInScope, error, rootRef, sentinelRef, loadMore }
}
